import {
  AUTH_COOKIE_DOMAIN,
  BZNAV_ACCESS_TOKEN,
  BZNAV_AUTH_SESSION,
  BZNAV_SIGN_IN_COMPLETE_EVENT_SENT_KEY,
  BZNAV_USER_DATA,
  BznavAppIds,
  deleteCookieData,
  excludeNullishDataKeys,
  getCookieData,
  IS_CLIENT,
  META_PIXEL_BASIC_PROPERTIES,
  requestGetFetch,
  requestPostFetch,
  setCookieData
} from '@repo/common-utils';
import { sendBznavNativeMessage } from '@repo/platform';

import { initializeSessionMutationQuery, updateUserSessionMutationQuery } from '../queries';
import { AuthInitializeProps, RequestTokenProps } from '../types';
import { removeReturnUrl } from '../utils';

import { BaseAuthService } from './BaseAuthService';
import { SignService } from './SignService';

export class AuthService extends BaseAuthService {
  public cookieBroadcastTargetApps: BznavAppIds[] = [];
  private _gqlServerUrl: string = process.env.NEXT_PUBLIC_GQL_API_SERVER ?? 'https://gateway.dev.api.bznav.com/graphql';
  private _signService: SignService;

  constructor({ appId, clientId, signPaths }: AuthInitializeProps) {
    super(
      appId,
      clientId,
      'de0ghpbbj',
      signPaths ?? {
        signIn: '/signin',
        signUpInput: '/signup/input',
        signUpTerms: '/signup/terms',
        signOut: '/signout',
        forgotPassword: '/forgot-password',
        resetPassword: '/reset-password',
        ciCollectionRequest: '/ci-request',
        ciAuthentication: '/ci-authentication',
        defaultReturn: '/'
      },
      process.env.NEXT_PUBLIC_SSO_API_SERVER ?? 'https://sso.dev.api.zent.kr'
    );
    this.cookieBroadcastTargetApps = this._appId === 'bznav-care' ? ['bznav-care'] : ['bznav-refund', 'bznav-sena'];
    this._signService = new SignService(this._appId, this._clientId, this._clientSecret, this._signPaths);
    this.init();
  }

  get signService() {
    return this._signService;
  }

  private init() {
    if (!IS_CLIENT) return;

    // 브라우저를 종료했다가 다시 연 경우 로그아웃 처리
    if (!getCookieData(BZNAV_AUTH_SESSION)) {
      setCookieData(BZNAV_AUTH_SESSION, process.env.NEXT_PUBLIC_ZENV, { domain: AUTH_COOKIE_DOMAIN, path: '/' });
      this.removeAuthData();
    }
  }

  async initializeSession(mixpanelDeviceId: string = '') {
    const variables: Record<string, string | undefined> = {
      appId: this._appId,
      clientId: this._clientId,
      clientSessionId: this.clientSessionId,
      firstPage: typeof window !== 'undefined' ? window.location.href : '',
      sessionId: this.sessionId,
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : '',
      mixpanelDeviceId
    };
    if (typeof document !== 'undefined' && document.referrer) {
      variables.referrerHostname = new URL(document.referrer).hostname;
    }

    await requestPostFetch({
      url: this._gqlServerUrl,
      data: {
        query: initializeSessionMutationQuery,
        variables
      }
    }).then(() => {
      if (this.accessToken) {
        return this.initializeUserSession();
      }
    });
  }

  private initializeUserSession() {
    return requestPostFetch({
      url: this._gqlServerUrl,
      data: {
        query: updateUserSessionMutationQuery,
        variables: {
          appId: this._appId,
          clientId: this._clientId,
          sessionId: this.sessionId,
          clientSessionId: this.clientSessionId
        }
      },
      config: {
        authorization: `Bearer ${this.accessToken}`
      }
    });
  }

  removeAuthData() {
    if (!this.accessToken) return;
    this._encryptedUserData = undefined;
    this._decryptedUserData = undefined;
    deleteCookieData(BZNAV_ACCESS_TOKEN, this.authCookieOptions);
    deleteCookieData(BZNAV_USER_DATA, this.authCookieOptions);
    localStorage.removeItem(BZNAV_SIGN_IN_COMPLETE_EVENT_SENT_KEY);

    sendBznavNativeMessage('broadcastWebviewCookies', {
      label: 'userAuth',
      actionType: 'remove',
      targetApps: this.cookieBroadcastTargetApps,
      cookies: [
        { name: BZNAV_USER_DATA, value: '', options: this.authCookieOptions },
        { name: BZNAV_ACCESS_TOKEN, value: '', options: this.authCookieOptions }
      ]
    });
  }

  broadcastUserAuthCookies() {
    sendBznavNativeMessage('broadcastWebviewCookies', {
      label: 'userAuth',
      actionType: 'set',
      targetApps: this.cookieBroadcastTargetApps,
      cookies: [
        { name: BZNAV_ACCESS_TOKEN, value: this.accessToken, options: this.authCookieOptions },
        { name: BZNAV_USER_DATA, value: this._encryptedUserData, options: this.authCookieOptions },
        { name: BZNAV_AUTH_SESSION, value: process.env.NEXT_PUBLIC_ZENV, options: { domain: AUTH_COOKIE_DOMAIN, path: '/', secure: true } }
      ]
    });
  }

  refreshAuthCookies() {
    setCookieData(BZNAV_ACCESS_TOKEN, this.accessToken, this.authCookieOptions);
    setCookieData(BZNAV_USER_DATA, this._encryptedUserData, this.authCookieOptions);

    this.broadcastUserAuthCookies();
  }

  async requestToken({ grantType = 'authorization_code', code, utmData }: RequestTokenProps) {
    const metaProperties = sessionStorage.getItem(META_PIXEL_BASIC_PROPERTIES) ?? undefined;
    const excludedNullishUtm = excludeNullishDataKeys(utmData || {});

    const requestBody = new URLSearchParams({
      grant_type: grantType,
      code: code,
      ...excludedNullishUtm
    });

    if (metaProperties) {
      requestBody.append('metaProperties', metaProperties);
    }
    const result = await requestPostFetch({
      url: `${this._authSeverUrl}/oauth/v2/token`,
      data: requestBody.toString(),
      config: {
        contentType: 'application/x-www-form-urlencoded'
      }
    });

    const authData = await result.json();

    if (authData.hometaxCookies) {
      authData.user.hometaxCookies = JSON.stringify(authData.hometaxCookies);
    }

    if (authData.accessToken) {
      this.accessToken = authData.accessToken;
      this.userData = authData.user;
      await this.initializeUserSession();

      this.broadcastUserAuthCookies();
    }

    const successReturnUrl = this.signInSuccessUrl;
    removeReturnUrl();

    return { ...authData, successReturnUrl };
  }

  async getUser() {
    try {
      const response = await requestGetFetch({
        url: `${this._authSeverUrl}/oauth/user`,
        config: {
          authorization: `Bearer ${this.accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user data');
      }

      const result = await response.json();

      this.userData = result.user;
    } catch (error) {
      console.error('Error fetching user data:', error);
      this.removeAuthData();
    }
  }

  getTerms(termKeys: string[]) {
    return requestPostFetch({
      url: `${this._authSeverUrl}/terms`,
      data: {
        keys: termKeys
      },
      config: {
        authorization: `Bearer ${this.accessToken}`
      }
    });
  }

  getCiPrepare(requestType: string) {
    return requestPostFetch({
      url: `${this._authSeverUrl}/ci/prepare`,
      data: {
        requestType,
        appId: this._appId,
        returnUrl: `${window.location.origin}${this._signPaths.ciAuthentication}`
      },
      config: {
        authorization: `Bearer ${this.accessToken}`
      }
    });
  }

  updateTermsAgree(terms: { termKey: string; isApproved: boolean }[]) {
    return requestPostFetch({
      url: `${this._authSeverUrl}/update/user/terms`,
      data: {
        terms
      },
      config: {
        authorization: `Bearer ${this.accessToken}`
      }
    });
  }
}
